"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildTabbyInstance = exports.sanitizePayload = exports.LOG_NONE = exports.LOG_ALL = exports.CATEGORY_OWNERSHIP = exports.isSeverityAtLeast = exports.zLogSeverity = void 0;
const zod_1 = require("zod");
const lodash_1 = require("lodash");
const constants_1 = require("../constants");
const error_helpers_1 = require("../helpers/error.helpers");
// An `entry` model serializes to about 3000 characters.
// If this payload size is hit, we will trim it and emit a warning or the
// original severity, whichever is higher.
const MAX_PAYLOAD_LENGTH = 10_000;
class TabbyPrunedError extends Error {
    constructor(originalError) {
        super(originalError.message);
        this.name = "TabbyPrunedError";
        this.stack = originalError.stack;
    }
}
exports.zLogSeverity = zod_1.z.enum([
    "trace",
    "debug",
    "info",
    "warn",
    "error",
    "critical",
]);
const isSeverityAtLeast = (severity, base) => {
    return (exports.zLogSeverity.options.indexOf(severity) >= exports.zLogSeverity.options.indexOf(base));
};
exports.isSeverityAtLeast = isSeverityAtLeast;
// Feel free to add more categories here. Categories are a way to group
// semantic logs together.
/* eslint sort-keys: ["error", "asc", { allowLineSeparatedGroups: true }] */
exports.CATEGORY_OWNERSHIP = {
    adapter: "bork",
    boost_connect: "bork",
    chrome_extension: "bork",
    dialer: "bork",
    inbound_routing: "bork",
    message_queue: "bork",
    siphon: "bork",
    tab_opener: "bork",
    vonage_api: "bork",
    ai_roleplay: "coaching_insights",
    analytics: "coaching_insights",
    bigquery_load: "coaching_insights",
    coaching: "coaching_insights",
    insights: "coaching_insights",
    scorecards: "coaching_insights",
    teams: "coaching_insights",
    csv_lists: "code_blooded",
    guest_mode: "code_blooded",
    hangouts: "code_blooded",
    internal_notification: "code_blooded",
    live_listen: "code_blooded",
    media_devices: "code_blooded",
    mic_and_speaker_test_context: "code_blooded",
    salesfloor: "code_blooded",
    sso_config: "code_blooded",
    live_streams: "labs",
    auth: "no_ownership",
    dashboard: "no_ownership",
    infra_cluster: "no_ownership",
    infra_inactivity: "no_ownership",
    infra_job_queues: "no_ownership",
    infra_monitoring: "no_ownership",
    infra_purview: "no_ownership",
    infra_react: "no_ownership",
    infra_sapi: "no_ownership",
    infra_wubsub: "no_ownership",
    knock: "no_ownership",
    live_transfer: "no_ownership",
    performance_monitoring: "no_ownership",
    slack_alerts: "no_ownership",
    tabby: "no_ownership",
    // "test" is only used for testing.
    test: "no_ownership",
};
exports.LOG_ALL = Symbol("tabby:LOG_ALL");
exports.LOG_NONE = Symbol("tabby:LOG_NONE");
const mergeTags = (parentTags, childTags = {}) => {
    return { ...parentTags, ...(0, lodash_1.pickBy)(childTags, v => v !== undefined) };
};
(0, constants_1.typeCheck)();
(0, constants_1.typeCheck)();
const sanitizePayload = (severity, payload, opts, 
// Mostly for testing
maxPayloadLength = MAX_PAYLOAD_LENGTH) => {
    const tags = mergeTags(opts.tags, payload.tags);
    // N.B. This error should be unreachable, since TabbyPayload's typing prevents
    // a caller from not passing in a category if one isn't already stored.
    const category = payload.category ?? opts.storedCategory;
    if (category === undefined) {
        throw new constants_1.AssertionError("TabbyCategory missing.");
    }
    const owningTeam = exports.CATEGORY_OWNERSHIP[category];
    let serializedTags;
    try {
        serializedTags = JSON.stringify(tags);
    }
    catch (e) {
        return {
            severity: "error",
            payload: {
                logFramework: "tabby",
                message: "Tabby failed to serialize tags",
                owningTeam,
                category: "tabby",
                time: Date.now(),
                error: (0, error_helpers_1.toError)(e),
            },
        };
    }
    let effectiveSeverity = severity;
    let effectiveTags = tags;
    let effectiveCategory = category;
    if (serializedTags.length > maxPayloadLength) {
        if (!(0, exports.isSeverityAtLeast)(effectiveSeverity, "warn")) {
            effectiveSeverity = "warn";
        }
        effectiveTags = {
            pruned: serializedTags.slice(0, maxPayloadLength),
        };
        effectiveCategory = "tabby";
    }
    // At this point, if there is no error, we don't need to further process it.
    let effectiveError = payload.error;
    if (effectiveError === undefined) {
        return {
            severity: effectiveSeverity,
            payload: {
                logFramework: "tabby",
                message: payload.message,
                owningTeam,
                category: effectiveCategory,
                time: Date.now(),
                tags: effectiveTags,
            },
        };
    }
    let serializedError;
    try {
        serializedError = JSON.stringify(effectiveError);
    }
    catch (e) {
        return {
            severity: "error",
            payload: {
                logFramework: "tabby",
                message: "Tabby failed to serialize error",
                owningTeam,
                category: "tabby",
                time: Date.now(),
                error: (0, error_helpers_1.toError)(e),
                tags: {
                    originalError: {
                        name: effectiveError.name,
                        message: effectiveError.message,
                        stack: effectiveError.stack,
                    },
                },
            },
        };
    }
    if (serializedError.length > maxPayloadLength) {
        effectiveError = new TabbyPrunedError(effectiveError);
        if (!(0, exports.isSeverityAtLeast)(effectiveSeverity, "warn")) {
            effectiveSeverity = "warn";
        }
        effectiveCategory = "tabby";
    }
    return {
        severity: effectiveSeverity,
        payload: {
            logFramework: "tabby",
            message: payload.message,
            owningTeam,
            category: effectiveCategory,
            time: Date.now(),
            error: effectiveError,
            tags: effectiveTags,
        },
    };
};
exports.sanitizePayload = sanitizePayload;
const tabbyHandler = (severity, payload, opts) => {
    if (opts.minimumSeverity === exports.LOG_NONE)
        return;
    if (opts.minimumSeverity !== exports.LOG_ALL &&
        !(0, exports.isSeverityAtLeast)(severity, opts.minimumSeverity))
        return;
    const { payload: effectivePayload, severity: effectiveSeverity } = (0, exports.sanitizePayload)(severity, payload, opts);
    return opts.definition.adapter.log(effectiveSeverity, opts.definition.adapter.injectContext(effectivePayload, opts.definition.dependencies, opts.traceContext));
};
// Private; we don't want "parentTags" available in the public API.
// The public API only exposes "globalTags" for type-safety.
const _buildTabbyChildInstance = ({ definition, minimumSeverity, storedCategory, parentTags, traceContext: parentTraceContext, }) => {
    return {
        trace: payload => tabbyHandler("trace", payload, {
            definition,
            minimumSeverity,
            storedCategory,
            tags: parentTags,
            traceContext: parentTraceContext,
        }),
        debug: payload => tabbyHandler("debug", payload, {
            definition,
            minimumSeverity,
            storedCategory,
            tags: parentTags,
            traceContext: parentTraceContext,
        }),
        info: payload => tabbyHandler("info", payload, {
            definition,
            minimumSeverity,
            storedCategory,
            tags: parentTags,
            traceContext: parentTraceContext,
        }),
        warn: payload => tabbyHandler("warn", payload, {
            definition,
            minimumSeverity,
            storedCategory,
            tags: parentTags,
            traceContext: parentTraceContext,
        }),
        error: payload => tabbyHandler("error", payload, {
            definition,
            minimumSeverity,
            storedCategory,
            tags: parentTags,
            traceContext: parentTraceContext,
        }),
        critical: payload => tabbyHandler("critical", payload, {
            definition,
            minimumSeverity,
            storedCategory,
            tags: parentTags,
            traceContext: parentTraceContext,
        }),
        child: ({ category, tags, traceMetadata }) => {
            const traceMetadataDefinition = traceMetadata?.[definition.id];
            return _buildTabbyChildInstance({
                definition,
                minimumSeverity,
                storedCategory: category ?? storedCategory,
                parentTags: mergeTags(parentTags, tags),
                traceContext: traceMetadataDefinition === undefined ? parentTraceContext : (definition.adapter.getTraceContextFromMetadata(traceMetadataDefinition, definition.dependencies) ?? parentTraceContext),
            });
        },
    };
};
const buildTabbyInstance = ({ definition, minimumSeverity, globalTags, }) => {
    return _buildTabbyChildInstance({
        definition,
        minimumSeverity,
        storedCategory: undefined,
        parentTags: mergeTags(globalTags, {}),
        traceContext: null,
    });
};
exports.buildTabbyInstance = buildTabbyInstance;
//# sourceMappingURL=index.js.map